/**
 * Bulk send logic for PreparedDropsDraftsList.
 * For Media Devoted | Created By Ciro Autiero
 */

import type React from 'react';
import { bulkSendPreparedDropsStream } from '@/hooks';
import { formatSendErrorDescription, getApiErrorMessage } from '@/lib/utils';
import { getApiBaseUrl, getAuthToken } from '@/services/api';

export interface BulkSendStatus {
  running: boolean;
  total: number;
  sent: number;
  failed: number;
}

export interface BulkSendCallbacks {
  toastLoading: (title: string, desc?: string) => string;
  toastUpdate: (id: string, title: string, desc?: string) => void;
  toastDismiss: (id: string) => void;
  toastSuccess: (title: string, desc?: string) => void;
  toastError: (title: string, desc?: string) => void;
  setBulkSendStatus: React.Dispatch<React.SetStateAction<BulkSendStatus>>;
  setSelectedIds: React.Dispatch<React.SetStateAction<string[]>>;
  onBulkSendComplete?: (result: { sent: number; failed: number; errorMessages?: string[] }) => void;
}

export type BulkSendProgressCallback = (sent: number, failed: number) => void;

export async function runBulkSendViaCallback(
  selectedIds: string[],
  onBulkSend: (
    ids: string[],
    onProgress?: BulkSendProgressCallback,
  ) => Promise<{ sent: number; failed: number; errorMessages?: string[] }>,
  callbacks: BulkSendCallbacks,
): Promise<void> {
  const { toastLoading, toastUpdate, toastDismiss, toastSuccess, toastError, setBulkSendStatus, setSelectedIds, onBulkSendComplete } = callbacks;
  setBulkSendStatus({ running: true, total: selectedIds.length, sent: 0, failed: 0 });
  const total = selectedIds.length;
  const loadingId = toastLoading('Bulk send…', `0 of ${total} drafts`);
  const onProgress: BulkSendProgressCallback = (sent, failed) => {
    toastUpdate(
      loadingId,
      'Bulk send…',
      `${sent} of ${total} sent${failed > 0 ? `, ${failed} failed` : ''}`,
    );
    setBulkSendStatus((prev) => ({ ...prev, sent, failed }));
  };
  try {
    const result = await onBulkSend(selectedIds, onProgress);
    toastDismiss(loadingId);
    setBulkSendStatus((prev) => ({ ...prev, running: false, sent: result.sent, failed: result.failed }));
    onBulkSendComplete?.({ sent: result.sent, failed: result.failed, errorMessages: result.errorMessages });
    setSelectedIds([]);
    if (result.failed > 0) {
      toastError(
        'Bulk send finished with errors',
        formatSendErrorDescription({ sent: result.sent, failed: result.failed, errorMessages: result.errorMessages }),
      );
    } else if (result.sent > 0) {
      toastSuccess('Sent successfully', `${result.sent} drop${result.sent !== 1 ? 's' : ''} sent successfully.`);
    }
  } catch (e) {
    toastDismiss(loadingId);
    setBulkSendStatus((prev) => ({ ...prev, running: false }));
    toastError('Bulk send failed', getApiErrorMessage(e, 'Could not send drafts.'));
  }
}

export async function runBulkSendViaStream(
  countryKey: string,
  wiserId: number,
  selectedIds: string[],
  callbacks: BulkSendCallbacks,
): Promise<void> {
  const { toastLoading, toastUpdate, toastDismiss, toastSuccess, toastError, setBulkSendStatus, setSelectedIds, onBulkSendComplete } = callbacks;
  setBulkSendStatus({ running: true, total: selectedIds.length, sent: 0, failed: 0 });
  const total = selectedIds.length;
  const loadingId = toastLoading('Bulk send…', `0 of ${total} drafts`);
  const errorMessages: string[] = [];
  try {
    await bulkSendPreparedDropsStream(countryKey, wiserId, selectedIds, {
      getAuthToken,
      getApiBase: getApiBaseUrl,
      onEvent: (event) => {
        if (event.status === 'sent') {
          setBulkSendStatus((prev) => {
            const next = { ...prev, sent: prev.sent + 1 };
            toastUpdate(loadingId, 'Bulk send…', `${next.sent} of ${total} sent`);
            return next;
          });
          return;
        }
        if (event.status === 'error') {
          if (event.message) errorMessages.push(event.message);
          setBulkSendStatus((prev) => {
            const next = { ...prev, failed: prev.failed + 1 };
            toastUpdate(loadingId, 'Bulk send…', `${next.sent} sent, ${next.failed} failed`);
            return next;
          });
          return;
        }
        if (event.status === 'done') {
          toastDismiss(loadingId);
          setBulkSendStatus((prev) => ({
            ...prev,
            running: false,
            total: event.total,
            sent: event.sent,
            failed: event.failed,
          }));
          onBulkSendComplete?.({ sent: event.sent, failed: event.failed, errorMessages });
          setSelectedIds([]);
          if (event.failed > 0) {
            toastError(
              'Bulk send finished with errors',
              formatSendErrorDescription({
                sent: event.sent,
                failed: event.failed,
                errorMessages,
              }),
            );
          } else {
            toastSuccess(
              'Sent successfully',
              `${event.sent} drop${event.sent !== 1 ? 's' : ''} sent successfully.`,
            );
          }
          return;
        }
      },
    });
  } catch (e) {
    toastDismiss(loadingId);
    setBulkSendStatus((prev) => ({ ...prev, running: false }));
    toastError('Bulk send failed', getApiErrorMessage(e, 'Could not send drafts.'));
  }
}
